import React, { useEffect, useState } from "react";
import "./datatable.scss";
import axiosInstance from "../../api/axios";
import { toast } from "react-toastify";
import { previewAutoSchedule, getAvailableSlots, savePreSchedule, assignToReports, getAssignToReportsPreview, assignSingleToReport, getPairHistory } from "../../services/vivaAutoScheduleService";
import { getExaminers, getReviewers } from "../../services/instructorService";
import { getBatches } from "../../services/studentService";
import { getAllAnnouncements } from "../../services/announcementsService";

const PHASE_OPTIONS = ["ProjectProposal", "Phase1", "Phase2", "Phase3"];

// Rows without a thesis_status yet (advance-scheduled only) share no common id,
// so identity is derived from (student, announcement) instead.
const rowKey = (row) => (row.thesisStatusId ? `ts-${row.thesisStatusId}` : `pre-${row.studentId}-${row.announcementId}`);

const isDeadlineInCurrentOrPreviousMonth = (dateStr) => {
  if (!dateStr) return false;
  const deadline = new Date(dateStr);
  if (Number.isNaN(deadline.getTime())) return false;
  const now = new Date();
  const monthsDiff = (now.getFullYear() - deadline.getFullYear()) * 12 + (now.getMonth() - deadline.getMonth());
  return monthsDiff === 0 || monthsDiff === 1;
};

const isDeadlineOver = (dateStr) => {
  if (!dateStr) return false;
  const deadline = new Date(dateStr);
  if (Number.isNaN(deadline.getTime())) return false;
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  return deadline.getTime() < todayStart.getTime();
};

const timeBucketLabel = (timeStr) => {
  if (!timeStr) return "";
  const hour = parseInt(timeStr.split(":")[0], 10);
  if (Number.isNaN(hour)) return "";
  if (hour < 12) return "Morning";
  if (hour < 17) return "Afternoon";
  return "Evening";
};

const reasonLabel = (reason) => {
  if (reason === "manual") return "already assigned";
  if (reason === "continuity") return "reused from prior phase";
  if (reason === "similarity") return "topic/expertise match";
  return reason || "";
};

const THESIS_STATUS_LABELS = {
  WORK_IN_PROGRESS: "Work in progress",
  RESUBMISSION: "Resubmission",
  REPORT_REVISION_REQUIRED: "Revision required",
  PHASE_COMPLETED: "Phase completed",
};
const thesisStatusLabel = (status) => THESIS_STATUS_LABELS[status] || status || "—";

const Modal = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1300,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: 480, background: "#fff", borderRadius: 8, padding: 20, boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}>
        {children}
      </div>
    </div>
  );
};

const NameWithReason = ({ name, reason, extra }) => (
  <div>
    <div>{name}{extra ? `, ${extra}` : ""}</div>
    {reason && <div style={{ fontSize: 10, color: "#999" }}>{reasonLabel(reason)}</div>}
  </div>
);

const PreviousPairCell = ({ row }) => {
  if (!row.previousPairExaminerName || !row.previousPairReviewerName) {
    return <span style={{ color: "#999" }}>—</span>;
  }
  return (
    <div style={{ fontSize: 12 }}>
      {row.previousPairExaminerName} + {row.previousPairReviewerName}
      <div style={{ fontSize: 10, color: "#999" }}>
        {row.previousPairCount} session{row.previousPairCount === 1 ? "" : "s"} together
      </div>
    </div>
  );
};

// Examiner+reviewer often share free slots on several dates — show all of them, not just the one
// picked: black/bold for the slot actually scheduled here, grey for shared slots still free, and
// struck through for shared slots someone (this pair or a conflicting one) has already claimed.
const CommonSlotsCell = ({ row }) => {
  const slots = row.commonSlots || [];
  if (slots.length === 0) return <span style={{ color: "#999" }}>—</span>;
  return (
    <div style={{ maxHeight: 90, overflowY: "auto" }}>
      {slots.map((s, i) => (
        <div
          key={`${s.date}|${s.startTime}|${i}`}
          style={{
            fontSize: 11,
            whiteSpace: "nowrap",
            color: s.status === "scheduled" ? "#000" : "#999",
            fontWeight: s.status === "scheduled" ? 700 : 400,
            textDecoration: s.status === "used" ? "line-through" : "none",
          }}
        >
          {s.date} {s.startTime}
        </div>
      ))}
    </div>
  );
};

const PersonPicker = ({ value, onChange, options, placeholder }) => (
  <select
    id="styled-dropdown"
    value={value || ""}
    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
    style={{ width: "100%", fontSize: 12 }}
  >
    <option value="">{placeholder}</option>
    {options.map((o) => (
      <option key={o.id} value={o.id}>{o.name}</option>
    ))}
  </select>
);

export const VivaAutoSchedule = () => {
  const [phaseSelection, setPhaseSelection] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [batchSelection, setBatchSelection] = useState([]);
  const [mode, setMode] = useState("SLOT_BASED");
  const [durationMinutes, setDurationMinutes] = useState(30);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  const [savingRowId, setSavingRowId] = useState(null);
  const [examiners, setExaminers] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [manualSelections, setManualSelections] = useState({});

  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const [announcements, setAnnouncements] = useState([]);
  const [confirmedAnnouncementIds, setConfirmedAnnouncementIds] = useState([]);
  const [assigning, setAssigning] = useState(false);

  const [reportsPreview, setReportsPreview] = useState([]);
  const [loadingReportsPreview, setLoadingReportsPreview] = useState(false);
  const [assigningRowId, setAssigningRowId] = useState(null);
  const [showScheduleSection, setShowScheduleSection] = useState(true);

  const [pairHistory, setPairHistory] = useState([]);
  const [showPairHistory, setShowPairHistory] = useState(false);

  const [sortMode, setSortMode] = useState("datetime"); // "datetime" | "examiner"

  useEffect(() => {
    getExaminers().then((res) => setExaminers(res.data || [])).catch((err) => console.error(err));
    getReviewers().then((res) => setReviewers(res.data || [])).catch((err) => console.error(err));
    getBatches().then((res) => setBatchOptions(res.data || [])).catch((err) => console.error(err));
    getAllAnnouncements().then((res) => setAnnouncements(res.data || [])).catch((err) => console.error(err));
    getPairHistory().then((res) => setPairHistory(res.data || [])).catch((err) => console.error(err));
  }, []);

  const togglePhase = (phase) => {
    setPhaseSelection((prev) => prev.includes(phase) ? prev.filter((p) => p !== phase) : [...prev, phase]);
  };

  const toggleBatch = (batch) => {
    setBatchSelection((prev) => prev.includes(batch) ? prev.filter((b) => b !== batch) : [...prev, batch]);
  };

  const matchingAnnouncements = announcements.filter(
    (a) =>
      phaseSelection.includes(a.submissionType) &&
      batchSelection.includes(a.batch) &&
      isDeadlineInCurrentOrPreviousMonth(a.submissionDeadline)
  );

  useEffect(() => {
    setConfirmedAnnouncementIds(matchingAnnouncements.map((a) => a.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseSelection, batchSelection, announcements]);

  const toggleAnnouncement = (id) => {
    setConfirmedAnnouncementIds((prev) => prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]);
  };

  const confirmedAnnouncements = matchingAnnouncements.filter((a) => confirmedAnnouncementIds.includes(a.id));
  const allConfirmedDeadlinesOver = confirmedAnnouncements.length > 0
    && confirmedAnnouncements.every((a) => isDeadlineOver(a.submissionDeadline));
  // Looked up against the FULL announcement list (not the deadline-windowed matchingAnnouncements) so
  // the per-row deadline gate still works for students whose announcement falls outside that window.
  const announcementFor = (id) => announcements.find((a) => a.id === id);

  const loadReportsPreview = async () => {
    if (phaseSelection.length === 0 || batchSelection.length === 0) {
      setReportsPreview([]);
      return;
    }
    setLoadingReportsPreview(true);
    try {
      // Sourced from the same phase/batch selection bar Generate Schedule uses — not limited to
      // confirmed announcements, so a student is never missing here just because their announcement's
      // deadline fell outside the "current or previous month" window used for the confirm checklist.
      const res = await getAssignToReportsPreview(phaseSelection, batchSelection);
      setReportsPreview(res.data || []);
    } catch (err) {
      toast.error("Failed to load student report status");
    } finally {
      setLoadingReportsPreview(false);
    }
  };

  useEffect(() => {
    loadReportsPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseSelection, batchSelection]);

  const handleAssignToReports = async () => {
    if (confirmedAnnouncementIds.length === 0) {
      toast.error("Select at least one announcement to assign");
      return;
    }
    setAssigning(true);
    try {
      const res = await assignToReports(confirmedAnnouncementIds);
      toast.success(res.message || "Assigned to reports");
      await loadReportsPreview();
      if (hasRun) await runPreview();
    } catch (err) {
      toast.error("Failed to assign viva schedule to reports");
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignSingle = async (row) => {
    setAssigningRowId(rowKey(row));
    try {
      const res = await assignSingleToReport(row.thesisStatusId);
      toast.success(res.message || "Assigned");
      await loadReportsPreview();
      if (hasRun) await runPreview();
    } catch (err) {
      toast.error("Failed to assign viva schedule for this student");
    } finally {
      setAssigningRowId(null);
    }
  };

  const runPreview = async () => {
    if (phaseSelection.length === 0 || batchSelection.length === 0) {
      toast.error("Select at least one phase and one batch");
      return;
    }
    setLoading(true);
    try {
      const res = await previewAutoSchedule({ reportTypes: phaseSelection, batches: batchSelection, mode, durationMinutes });
      const data = res.data || [];
      setRows(data);
      setHasRun(true);
      setManualSelections((prev) => {
        const next = { ...prev };
        data.forEach((row) => {
          if (row.status === "unresolved" && !next[rowKey(row)]) {
            next[rowKey(row)] = {
              examinerId1: row.examinerId1 || null,
              examinerId2: row.examinerId2 || null,
              reviewerId1: row.reviewerId1 || null,
              reviewerId2: row.reviewerId2 || null,
            };
          }
        });
        return next;
      });
    } catch (err) {
      toast.error("Failed to generate schedule proposal");
    } finally {
      setLoading(false);
    }
  };

  const updateManualSelection = (key, field, value) => {
    setManualSelections((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const savePairing = async (row) => {
    const selection = manualSelections[rowKey(row)] || {};
    if (!selection.examinerId1 || !selection.reviewerId1) {
      toast.error("Examiner 1 and Reviewer 1 are required");
      return;
    }
    setSavingRowId(rowKey(row));
    try {
      if (row.thesisStatusId) {
        await axiosInstance.put("/updateThesisSubmissionExaminers", {
          id: row.thesisStatusId,
          studentId: row.studentId,
          examinerId1: selection.examinerId1,
          examinerId2: selection.examinerId2 || null,
          reviewerId1: selection.reviewerId1,
          reviewerId2: selection.reviewerId2 || null,
        });
      } else {
        await savePreSchedule({
          studentId: row.studentId,
          announcementId: row.announcementId,
          examinerId1: selection.examinerId1,
          examinerId2: selection.examinerId2 || null,
          reviewerId1: selection.reviewerId1,
          reviewerId2: selection.reviewerId2 || null,
        });
      }
      toast.success("Panel saved. It will auto-schedule once a common slot is available.");
      await runPreview();
      await loadReportsPreview();
    } catch (err) {
      console.error(`Failed to save pairing for student ${row.studentId}`, err);
      toast.error("Failed to save panel");
    } finally {
      setSavingRowId(null);
    }
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setEditForm({
      examinerId1: row.examinerId1 || null,
      examinerId2: row.examinerId2 || null,
      reviewerId1: row.reviewerId1 || null,
      reviewerId2: row.reviewerId2 || null,
      date: row.date || "",
      startTime: row.startTime || "",
      endTime: row.endTime || "",
    });
    setAvailableSlots([]);
  };

  const closeEdit = () => {
    setEditingRow(null);
    setEditForm({});
    setAvailableSlots([]);
  };

  // Students-under-announcements rows use different field names (current*/pre*) than the Generate
  // Schedule result rows openEdit expects — map to the same shape, preferring whatever's already
  // assigned on the report and falling back to the saved advance schedule.
  const openEditFromPreviewRow = (row) => {
    openEdit({
      thesisStatusId: row.thesisStatusId,
      studentId: row.studentId,
      announcementId: row.announcementId,
      studentName: row.studentName,
      enrollmentNumber: row.enrollmentNumber,
      examinerId1: row.currentExaminerId1 || row.preExaminerId1 || null,
      examinerId2: row.currentExaminerId2 || row.preExaminerId2 || null,
      reviewerId1: row.currentReviewerId1 || row.preReviewerId1 || null,
      reviewerId2: row.currentReviewerId2 || row.preReviewerId2 || null,
      date: row.currentDate || row.preDate || "",
      startTime: row.currentStartTime || row.preStartTime || "",
      endTime: row.currentEndTime || row.preEndTime || "",
    });
  };

  useEffect(() => {
    if (!editingRow || !editForm.examinerId1 || !editForm.reviewerId1) {
      setAvailableSlots([]);
      return;
    }
    setLoadingSlots(true);
    getAvailableSlots({
      examinerId1: editForm.examinerId1,
      examinerId2: editForm.examinerId2,
      reviewerId1: editForm.reviewerId1,
      reviewerId2: editForm.reviewerId2,
      durationMinutes,
    })
      .then((res) => setAvailableSlots(res.data || []))
      .catch((err) => console.error(err))
      .finally(() => setLoadingSlots(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editForm.examinerId1, editForm.examinerId2, editForm.reviewerId1, editForm.reviewerId2]);

  const saveEdit = async () => {
    if (!editForm.examinerId1 || !editForm.reviewerId1) {
      toast.error("Examiner 1 and Reviewer 1 are required");
      return;
    }
    setSavingEdit(true);
    try {
      if (editingRow.thesisStatusId) {
        await axiosInstance.put("/updateThesisSubmissionExaminers", {
          id: editingRow.thesisStatusId,
          studentId: editingRow.studentId,
          examinerId1: editForm.examinerId1,
          examinerId2: editForm.examinerId2 || null,
          reviewerId1: editForm.reviewerId1,
          reviewerId2: editForm.reviewerId2 || null,
        });
        // A slot is optional (e.g. expertise-only mode) — only touch the viva_schedule when one was picked.
        if (editForm.date) {
          await axiosInstance.post("/addVivaSchedule", {
            thesisStatusId: editingRow.thesisStatusId,
            date: editForm.date,
            startTime: editForm.startTime,
            endTime: editForm.endTime,
          });
        }
      } else {
        await savePreSchedule({
          studentId: editingRow.studentId,
          announcementId: editingRow.announcementId,
          examinerId1: editForm.examinerId1,
          examinerId2: editForm.examinerId2 || null,
          reviewerId1: editForm.reviewerId1,
          reviewerId2: editForm.reviewerId2 || null,
          date: editForm.date || null,
          startTime: editForm.startTime || null,
          endTime: editForm.endTime || null,
        });
      }
      toast.success("Viva updated");
      closeEdit();
      await loadReportsPreview();
      if (hasRun) await runPreview();
    } catch (err) {
      console.error("Failed to save edit", err);
      toast.error("Failed to save changes");
    } finally {
      setSavingEdit(false);
    }
  };

  const resolvedScheduled = rows.filter((r) => r.status === "resolved" && r.date);
  const resolvedUnscheduled = rows.filter((r) => r.status === "resolved" && !r.date);
  const unresolved = rows.filter((r) => r.status === "unresolved");

  const groups = {};
  resolvedScheduled.forEach((row) => {
    const key = `${row.date}|${row.startTime}|${row.endTime}`;
    if (!groups[key]) groups[key] = { date: row.date, startTime: row.startTime, endTime: row.endTime, rows: [] };
    groups[key].rows.push(row);
  });
  const sortedGroups = Object.values(groups).sort((a, b) =>
    `${a.date}${a.startTime}`.localeCompare(`${b.date}${b.startTime}`)
  );

  const examinerGroups = {};
  resolvedScheduled.forEach((row) => {
    const key = row.examiner1Name || "Unassigned";
    if (!examinerGroups[key]) examinerGroups[key] = { examinerName: key, rows: [] };
    examinerGroups[key].rows.push(row);
  });
  const sortedExaminerGroups = Object.values(examinerGroups)
    .map((g) => ({
      ...g,
      rows: [...g.rows].sort((a, b) => `${a.date}${a.startTime}`.localeCompare(`${b.date}${b.startTime}`)),
    }))
    .sort((a, b) => a.examinerName.localeCompare(b.examinerName));

  const renderScheduleTable = (tableRows, { showDateTime }) => (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr>
          {[
            "Student", "Roll No",
            ...(showDateTime ? ["Date & Time"] : []),
            "Title", "Keywords", "Examiner", "Expertise", "Reviewer", "Previous Pair", "Common Slots", "",
          ].map((h) => (
            <th key={h} style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "6px 8px" }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {tableRows.map((row) => (
          <tr key={rowKey(row)}>
            <td style={{ padding: "6px 8px" }}>{row.studentName}</td>
            <td style={{ padding: "6px 8px" }}>{row.enrollmentNumber}</td>
            {showDateTime && (
              <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                {row.date}
                <div style={{ fontSize: 11, color: "#666" }}>{timeBucketLabel(row.startTime)} · {row.startTime}–{row.endTime}</div>
              </td>
            )}
            <td style={{ padding: "6px 8px" }}>{row.thesisTitle}</td>
            <td style={{ padding: "6px 8px", color: "#666" }}>{row.thesisKeywords || "—"}</td>
            <td style={{ padding: "6px 8px" }}>
              <NameWithReason name={row.examiner1Name} reason={row.examiner1Reason} extra={row.examiner2Name} />
            </td>
            <td style={{ padding: "6px 8px", color: "#666" }}>{row.examiner1Expertise || "—"}</td>
            <td style={{ padding: "6px 8px" }}>
              <NameWithReason name={row.reviewer1Name} reason={row.reviewer1Reason} extra={row.reviewer2Name} />
            </td>
            <td style={{ padding: "6px 8px" }}>
              <PreviousPairCell row={row} />
            </td>
            <td style={{ padding: "6px 8px" }}>
              <CommonSlotsCell row={row} />
            </td>
            <td style={{ padding: "6px 8px" }}>
              <button style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => openEdit(row)}>Edit</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="datatable" style={{ padding: 16 }}>
      <div className="datatableTitle">Auto-Schedule Viva</div>

      <div style={{ display: "flex", gap: 32, flexWrap: "wrap", padding: "12px 0", borderBottom: "1px solid #eee", marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Phase</div>
          {PHASE_OPTIONS.map((phase) => (
            <label key={phase} style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
              <input type="checkbox" checked={phaseSelection.includes(phase)} onChange={() => togglePhase(phase)} /> {phase}
            </label>
          ))}
        </div>

        <div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Batch</div>
          <div style={{ maxHeight: 140, overflowY: "auto" }}>
            {batchOptions.map((batch) => (
              <label key={batch} style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
                <input type="checkbox" checked={batchSelection.includes(batch)} onChange={() => toggleBatch(batch)} /> {batch}
              </label>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Scheduling mode</div>
          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
            <input type="radio" name="mode" checked={mode === "SLOT_BASED"} onChange={() => setMode("SLOT_BASED")} /> Schedule with viva slots
          </label>
          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
            <input type="radio" name="mode" checked={mode === "EXPERTISE_ONLY"} onChange={() => setMode("EXPERTISE_ONLY")} /> Match by expertise only (no slot/date)
          </label>
        </div>

        <div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Viva duration (minutes)</div>
          <input
            type="number"
            min={5}
            step={5}
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(Math.max(5, Number(e.target.value) || 30))}
            style={{ width: 90, fontSize: 13 }}
          />
        </div>

        <div style={{ alignSelf: "flex-end" }}>
          <button onClick={runPreview} disabled={loading}>{loading ? "Generating..." : "Generate Schedule"}</button>
        </div>
      </div>

      {phaseSelection.length > 0 && batchSelection.length > 0 && (
        <div style={{ padding: "12px 0", borderBottom: "1px solid #eee", marginBottom: 16 }}>
          <button onClick={() => setShowScheduleSection((v) => !v)} style={{ fontSize: 13, fontWeight: 600 }}>
            {showScheduleSection ? "Hide" : "Show"} Viva Scheduling &amp; Assignment
          </button>

          {showScheduleSection && (
            <div style={{ marginTop: 12 }}>
              {matchingAnnouncements.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                    Announcements this applies to ({confirmedAnnouncementIds.length} of {matchingAnnouncements.length} selected)
                  </div>
                  <div style={{ maxHeight: 140, overflowY: "auto", marginBottom: 8 }}>
                    {matchingAnnouncements.map((a) => (
                      <label key={a.id} style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
                        <input
                          type="checkbox"
                          checked={confirmedAnnouncementIds.includes(a.id)}
                          onChange={() => toggleAnnouncement(a.id)}
                        />{" "}
                        {a.title || `${a.batch || ""} ${a.submissionType || ""}`} {a.termType ? `(${a.termType})` : ""}
                        {a.submissionDeadline && (
                          <span style={{ color: isDeadlineOver(a.submissionDeadline) ? "#2e7d32" : "#c62828", fontSize: 11 }}>
                            {" "}— deadline {a.submissionDeadline} {isDeadlineOver(a.submissionDeadline) ? "(over)" : "(not yet over)"}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                  <button onClick={handleAssignToReports} disabled={assigning || !allConfirmedDeadlinesOver}>
                    {assigning ? "Assigning..." : "Assign to Reports (checked announcements)"}
                  </button>
                  {!allConfirmedDeadlinesOver && (
                    <p style={{ color: "#c62828", fontSize: 12, marginTop: 6 }}>
                      Assign to Reports is disabled until the submission deadline has passed for every checked announcement.
                    </p>
                  )}
                  <p style={{ color: "#666", fontSize: 12, marginTop: 6 }}>
                    Pushes any saved advance schedule onto submitted reports under the checked announcement(s), overwriting
                    their current examiner/reviewer/date.
                  </p>
                </>
              )}

              <div style={{ fontWeight: 600, fontSize: 13, margin: "16px 0 6px" }}>
                Students for this phase/batch selection {loadingReportsPreview ? "(loading...)" : `(${reportsPreview.length})`}
              </div>
              {reportsPreview.length > 0 && (
                <div style={{ maxHeight: 360, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr>
                        {["Student", "Thesis Status", "Report Status", "Current Panel", "Saved Panel", ""].map((h) => (
                          <th key={h} style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "6px 8px" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {reportsPreview.map((row) => {
                        const ann = announcementFor(row.announcementId);
                        const deadlineOver = ann ? isDeadlineOver(ann.submissionDeadline) : false;
                        const canAssign = row.isSubmitted && row.hasPreSchedule && deadlineOver;
                        const isAssigning = assigningRowId === rowKey(row);
                        return (
                          <tr key={rowKey(row)} style={{ borderBottom: "1px solid #f0f0f0" }}>
                            <td style={{ padding: "6px 8px", verticalAlign: "top" }}>
                              <div>{row.studentName}</div>
                              <div style={{ color: "#666", fontSize: 11 }}>{row.enrollmentNumber}</div>
                            </td>
                            <td style={{ padding: "6px 8px", verticalAlign: "top" }}>
                              {thesisStatusLabel(row.thesisStatus)}
                            </td>
                            <td style={{ padding: "6px 8px", verticalAlign: "top" }}>
                              {row.isSubmitted ? (
                                <span style={{ color: "#2e7d32" }}>Submitted{row.dateSubmitted ? ` on ${row.dateSubmitted.slice(0, 10)}` : ""}</span>
                              ) : (
                                <span style={{ color: "#c62828" }}>Not submitted yet</span>
                              )}
                            </td>
                            <td style={{ padding: "6px 8px", verticalAlign: "top" }}>
                              {row.currentExaminerName1 || row.currentReviewerName1 ? (
                                <>
                                  <div>{row.currentExaminerName1}{row.currentExaminerName2 ? `, ${row.currentExaminerName2}` : ""}</div>
                                  <div>{row.currentReviewerName1}{row.currentReviewerName2 ? `, ${row.currentReviewerName2}` : ""}</div>
                                  {row.currentDate && <div style={{ color: "#666", fontSize: 11 }}>{row.currentDate} {row.currentStartTime}</div>}
                                </>
                              ) : "—"}
                            </td>
                            <td style={{ padding: "6px 8px", verticalAlign: "top" }}>
                              {row.hasPreSchedule ? (
                                <>
                                  <div>{row.preExaminerName1}{row.preExaminerName2 ? `, ${row.preExaminerName2}` : ""}</div>
                                  <div>{row.preReviewerName1}{row.preReviewerName2 ? `, ${row.preReviewerName2}` : ""}</div>
                                  {row.preDate && <div style={{ color: "#666", fontSize: 11 }}>{row.preDate} {row.preStartTime}</div>}
                                </>
                              ) : <span style={{ color: "#999" }}>No panel saved</span>}
                            </td>
                            <td style={{ padding: "6px 8px", verticalAlign: "top", whiteSpace: "nowrap" }}>
                              <button onClick={() => openEditFromPreviewRow(row)}>Edit</button>{" "}
                              <button onClick={() => handleAssignSingle(row)} disabled={isAssigning || !canAssign}>
                                {isAssigning ? "Assigning..." : "Assign"}
                              </button>
                              {!canAssign && (
                                <div style={{ color: "#999", fontSize: 10, marginTop: 2 }}>
                                  {!row.isSubmitted ? "not submitted" : !row.hasPreSchedule ? "no panel saved" : !deadlineOver ? "deadline not over" : ""}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {pairHistory.length > 0 && (
        <div style={{ padding: "12px 0", borderBottom: "1px solid #eee", marginBottom: 16 }}>
          <button onClick={() => setShowPairHistory((v) => !v)} style={{ fontSize: 13 }}>
            {showPairHistory ? "Hide" : "Show"} Historical Examiner-Reviewer Pairings ({pairHistory.length})
          </button>
          {showPairHistory && (
            <div style={{ maxHeight: 260, overflowY: "auto", marginTop: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {["Examiner", "Reviewer", "Sessions Together"].map((h) => (
                      <th key={h} style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "6px 8px" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pairHistory.map((p) => (
                    <tr key={`${p.examinerId}-${p.reviewerId}`}>
                      <td style={{ padding: "6px 8px" }}>{p.examinerName}</td>
                      <td style={{ padding: "6px 8px" }}>{p.reviewerName}</td>
                      <td style={{ padding: "6px 8px" }}>{p.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p style={{ color: "#666", fontSize: 12, marginTop: 6 }}>
            Every (examiner, reviewer) combination that has sat together across all past viva sessions, most-frequent first.
          </p>
        </div>
      )}

      {!hasRun && <p style={{ color: "#666", fontSize: 13 }}>Select a phase and batch, then click Generate Schedule.</p>}

      {hasRun && sortedGroups.length === 0 && resolvedUnscheduled.length === 0 && unresolved.length === 0 && (
        <p style={{ color: "#666", fontSize: 13 }}>No pending vivas found for this selection.</p>
      )}

      {sortedGroups.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, marginRight: 10 }}>Sort scheduled sessions by:</span>
          <label style={{ fontSize: 13, marginRight: 14 }}>
            <input type="radio" name="sortMode" checked={sortMode === "datetime"} onChange={() => setSortMode("datetime")} />{" "}
            Date &amp; slot time (Morning/Afternoon/Evening)
          </label>
          <label style={{ fontSize: 13 }}>
            <input type="radio" name="sortMode" checked={sortMode === "examiner"} onChange={() => setSortMode("examiner")} />{" "}
            Examiner
          </label>
        </div>
      )}

      {sortMode === "datetime" && sortedGroups.map((group) => (
        <div key={`${group.date}|${group.startTime}`} style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, background: "#f0f4f8", padding: "6px 10px", borderRadius: 4 }}>
            {group.date} &nbsp;·&nbsp; {timeBucketLabel(group.startTime)} &nbsp;·&nbsp; {group.startTime} – {group.endTime}
          </div>
          {renderScheduleTable(group.rows, { showDateTime: false })}
        </div>
      ))}

      {sortMode === "examiner" && sortedExaminerGroups.map((group) => (
        <div key={group.examinerName} style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, background: "#f0f4f8", padding: "6px 10px", borderRadius: 4 }}>
            {group.examinerName} &nbsp;·&nbsp; {group.rows.length} session{group.rows.length === 1 ? "" : "s"}
          </div>
          {renderScheduleTable(group.rows, { showDateTime: true })}
        </div>
      ))}

      {resolvedUnscheduled.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, background: "#fff3e0", padding: "6px 10px", borderRadius: 4 }}>
            Panel assigned — no viva slot yet
          </div>
          <p style={{ color: "#666", fontSize: 12, margin: "4px 0" }}>
            Click Edit to change the examiner/reviewer or assign a slot.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {["Student", "Roll No", "Title", "Keywords", "Examiner", "Expertise", "Reviewer", "Previous Pair", "Common Slots", ""].map((h) => (
                  <th key={h} style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "6px 8px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {resolvedUnscheduled.map((row) => (
                <tr key={rowKey(row)}>
                  <td style={{ padding: "6px 8px" }}>{row.studentName}</td>
                  <td style={{ padding: "6px 8px" }}>{row.enrollmentNumber}</td>
                  <td style={{ padding: "6px 8px" }}>{row.thesisTitle}</td>
                  <td style={{ padding: "6px 8px", color: "#666" }}>{row.thesisKeywords || "—"}</td>
                  <td style={{ padding: "6px 8px" }}>
                    <NameWithReason name={row.examiner1Name} reason={row.examiner1Reason} extra={row.examiner2Name} />
                  </td>
                  <td style={{ padding: "6px 8px", color: "#666" }}>{row.examiner1Expertise || "—"}</td>
                  <td style={{ padding: "6px 8px" }}>
                    <NameWithReason name={row.reviewer1Name} reason={row.reviewer1Reason} extra={row.reviewer2Name} />
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    <PreviousPairCell row={row} />
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    <CommonSlotsCell row={row} />
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    <button style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => openEdit(row)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {unresolved.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, background: "#fdecea", padding: "6px 10px", borderRadius: 4 }}>
            Unresolved — pick examiner/reviewer manually
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {["Student", "Roll No", "Title", "Keywords", "Examiner 1", "Examiner 2", "Reviewer 1", "Reviewer 2", "Previous Pair", "Common Slots", "Reason", ""].map((h) => (
                  <th key={h} style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "6px 8px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {unresolved.map((row) => {
                const key = rowKey(row);
                const selection = manualSelections[key] || {};
                return (
                  <tr key={key}>
                    <td style={{ padding: "6px 8px" }}>{row.studentName}</td>
                    <td style={{ padding: "6px 8px" }}>{row.enrollmentNumber}</td>
                    <td style={{ padding: "6px 8px" }}>{row.thesisTitle}</td>
                    <td style={{ padding: "6px 8px", color: "#666" }}>{row.thesisKeywords || "—"}</td>
                    <td style={{ padding: "6px 8px", minWidth: 140 }}>
                      <PersonPicker value={selection.examinerId1} options={examiners} placeholder="Examiner 1"
                        onChange={(v) => updateManualSelection(key, "examinerId1", v)} />
                    </td>
                    <td style={{ padding: "6px 8px", minWidth: 140 }}>
                      <PersonPicker value={selection.examinerId2} options={examiners} placeholder="Examiner 2"
                        onChange={(v) => updateManualSelection(key, "examinerId2", v)} />
                    </td>
                    <td style={{ padding: "6px 8px", minWidth: 140 }}>
                      <PersonPicker value={selection.reviewerId1} options={reviewers} placeholder="Reviewer 1"
                        onChange={(v) => updateManualSelection(key, "reviewerId1", v)} />
                    </td>
                    <td style={{ padding: "6px 8px", minWidth: 140 }}>
                      <PersonPicker value={selection.reviewerId2} options={reviewers} placeholder="Reviewer 2"
                        onChange={(v) => updateManualSelection(key, "reviewerId2", v)} />
                    </td>
                    <td style={{ padding: "6px 8px" }}>
                      <PreviousPairCell row={row} />
                    </td>
                    <td style={{ padding: "6px 8px" }}>
                      <CommonSlotsCell row={row} />
                    </td>
                    <td style={{ padding: "6px 8px", fontSize: 11, color: "#666", maxWidth: 200 }}>{row.reason}</td>
                    <td style={{ padding: "6px 8px" }}>
                      <button
                        style={{ fontSize: 11, padding: "2px 8px" }}
                        disabled={savingRowId === key}
                        onClick={() => savePairing(row)}
                      >
                        {savingRowId === key ? "Saving..." : "Save Panel"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!editingRow} onClose={closeEdit}>
        {editingRow && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontWeight: 600 }}>{editingRow.studentName} ({editingRow.enrollmentNumber})</div>

            <div>
              <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Examiner 1</label>
              <PersonPicker value={editForm.examinerId1} options={examiners} placeholder="Select examiner"
                onChange={(v) => setEditForm((p) => ({ ...p, examinerId1: v, date: "", startTime: "", endTime: "" }))} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Examiner 2</label>
              <PersonPicker value={editForm.examinerId2} options={examiners} placeholder="Select examiner (optional)"
                onChange={(v) => setEditForm((p) => ({ ...p, examinerId2: v, date: "", startTime: "", endTime: "" }))} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Reviewer 1</label>
              <PersonPicker value={editForm.reviewerId1} options={reviewers} placeholder="Select reviewer"
                onChange={(v) => setEditForm((p) => ({ ...p, reviewerId1: v, date: "", startTime: "", endTime: "" }))} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Reviewer 2</label>
              <PersonPicker value={editForm.reviewerId2} options={reviewers} placeholder="Select reviewer (optional)"
                onChange={(v) => setEditForm((p) => ({ ...p, reviewerId2: v, date: "", startTime: "", endTime: "" }))} />
            </div>

            <div>
              <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Available slot</label>
              <select
                id="styled-dropdown"
                style={{ width: "100%" }}
                value={editForm.date ? `${editForm.date}|${editForm.startTime}|${editForm.endTime}` : ""}
                onChange={(e) => {
                  const [date, startTime, endTime] = e.target.value.split("|");
                  setEditForm((p) => ({ ...p, date, startTime, endTime }));
                }}
                disabled={!editForm.examinerId1 || !editForm.reviewerId1}
              >
                <option value="">
                  {loadingSlots ? "Loading slots..." : availableSlots.length === 0 ? "No common free slot for this pair" : "Select a slot"}
                </option>
                {availableSlots.map((s) => (
                  <option key={`${s.date}|${s.startTime}`} value={`${s.date}|${s.startTime}|${s.endTime}`}>
                    {s.date} — {s.startTime} to {s.endTime}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
                Or enter a date/time manually (e.g. when no viva slot preferences have been submitted yet)
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="date"
                  value={editForm.date || ""}
                  onChange={(e) => setEditForm((p) => ({ ...p, date: e.target.value }))}
                  style={{ flex: 1, padding: 6 }}
                />
                <input
                  type="time"
                  value={editForm.startTime || ""}
                  onChange={(e) => setEditForm((p) => ({ ...p, startTime: e.target.value }))}
                  style={{ flex: 1, padding: 6 }}
                />
                <input
                  type="time"
                  value={editForm.endTime || ""}
                  onChange={(e) => setEditForm((p) => ({ ...p, endTime: e.target.value }))}
                  style={{ flex: 1, padding: 6 }}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button onClick={saveEdit} disabled={savingEdit}>{savingEdit ? "Saving..." : "Save"}</button>
              <button onClick={closeEdit}>Cancel</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VivaAutoSchedule;
